import React, { useContext } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography,
  TextField, Grid, Stack, Paper, Chip, CircularProgress, InputAdornment, IconButton, Tooltip,
} from '@mui/material';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import { createWindowedPaper, WindowedControlsContext } from '../../../../components/Dialogs/WindowedPaper';
import LineItemsTable from '../../../../components/Documents/LineItemsTable';
import SummaryBox from '../../../../components/Documents/SummaryBox';

const DOC_TYPE_LABEL = {
  quotation: 'ใบเสนอราคา',
  delivery_note: 'ใบส่งงาน',
  billing_note: 'ใบวางบิล',
  receipt: 'ใบเสร็จรับเงิน',
  tax_invoice: 'ใบกำกับภาษี',
};

const WindowedPaper = createWindowedPaper({
  defaultSize: { width: 900, height: 760 },
  minSize: { width: 640, height: 480 },
});

function MaximizeButton() {
  const { maximized, toggleMaximize } = useContext(WindowedControlsContext);
  return (
    <Tooltip title={maximized ? 'คืนขนาดเดิม' : 'ขยายเต็มจอ'}>
      <IconButton size="small" onClick={toggleMaximize}>
        {maximized ? <FullscreenExitIcon fontSize="small" /> : <FullscreenIcon fontSize="small" />}
      </IconButton>
    </Tooltip>
  );
}

// Modal สร้างเอกสาร Sale จากโอกาสทางธุรกิจโดยตรง — แทนการ navigate ไปหน้าเต็ม
// /sale/documents/new?...&opportunity_id=... เดิม ไม่มี card เลือกโครงการเพราะยัง
// อยู่ช่วง marketing (ยังไม่มีโครงการจริง) ข้อมูลลูกค้า/ผู้ติดต่อ auto-fill มาจาก
// Contact ที่ผูกกับ opportunity ให้แล้ว (ดู useCreateSaleDocument.js) ลากย้าย/ปรับขนาด
// ได้ทุกทิศทาง + ขยายเต็มจอได้ — ดู components/Dialogs/WindowedPaper.js
export function CreateSaleDocumentModal({ open, docType, onClose, doc }) {
  const {
    form, setField, errors, saving, previewingNumber, uploadingPO,
    poInputRef, handleUploadPO, handleItemsChange, submit,
  } = doc;

  return (
    <Dialog open={open} onClose={onClose} maxWidth={false} PaperComponent={WindowedPaper} PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 700, flexShrink: 0 }}>
        <DescriptionOutlinedIcon color="secondary" />
        <Typography variant="inherit" sx={{ flex: 1 }}>
          สร้าง{DOC_TYPE_LABEL[docType] || 'เอกสาร'}
        </Typography>
        <MaximizeButton />
      </DialogTitle>

      <DialogContent dividers sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        <Stack spacing={2.5}>
          <Paper variant="outlined" sx={{ borderRadius: 2, p: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="เลขที่เอกสาร"
                  size="small"
                  value={form.document_number}
                  onChange={(e) => setField('document_number', e.target.value)}
                  placeholder="กำลังโหลด..."
                  disabled={previewingNumber}
                  InputProps={{
                    startAdornment: previewingNumber
                      ? <InputAdornment position="start"><CircularProgress size={14} /></InputAdornment>
                      : null,
                  }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="วันที่ *"
                  type="date"
                  size="small"
                  value={form.date}
                  onChange={(e) => setField('date', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="ครบกำหนด"
                  type="date"
                  size="small"
                  value={form.due_date}
                  onChange={(e) => setField('due_date', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
            </Grid>
          </Paper>

          <Grid container spacing={2.5}>
            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={{ borderRadius: 2, p: 2, height: '100%' }}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5, color: 'text.secondary' }}>
                  ข้อมูลลูกค้า
                </Typography>
                <Stack spacing={1.5}>
                  <TextField
                    fullWidth label="ชื่อลูกค้า" size="small"
                    value={form.customer_name}
                    onChange={(e) => setField('customer_name', e.target.value)}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                  <TextField
                    fullWidth label="ที่อยู่ลูกค้า" size="small" multiline rows={2}
                    value={form.customer_address}
                    onChange={(e) => setField('customer_address', e.target.value)}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                  <TextField
                    fullWidth label="เลขผู้เสียภาษี" size="small"
                    value={form.customer_tax_id}
                    onChange={(e) => setField('customer_tax_id', e.target.value)}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Stack>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={{ borderRadius: 2, p: 2, height: '100%' }}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5, color: 'text.secondary' }}>
                  รายละเอียดงาน
                </Typography>
                <Stack spacing={1.5}>
                  <TextField
                    fullWidth label="ชื่องาน / หัวข้อ" size="small" multiline rows={2}
                    value={form.topic}
                    onChange={(e) => setField('topic', e.target.value)}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                  <TextField
                    fullWidth label="ผู้ติดต่อ" size="small"
                    value={form.contact_name}
                    onChange={(e) => setField('contact_name', e.target.value)}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                  <TextField
                    fullWidth label="อีเมลผู้ติดต่อ" size="small"
                    value={form.contact_email}
                    onChange={(e) => setField('contact_email', e.target.value)}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                  <TextField
                    fullWidth label="ผู้ขาย *" size="small"
                    value={form.seller}
                    onChange={(e) => setField('seller', e.target.value)}
                    error={!!errors.seller}
                    helperText={errors.seller}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Stack>
              </Paper>
            </Grid>
          </Grid>

          {docType === 'delivery_note' && (
            <Paper
              variant="outlined"
              sx={{
                borderRadius: 2,
                p: 2,
                borderColor: errors.po_document_url ? 'error.main' : 'divider',
                borderWidth: errors.po_document_url ? 2 : 1,
              }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Stack direction="row" alignItems="center" gap={1}>
                  <Typography variant="subtitle2" fontWeight={700}>แนบใบ PO จากลูกค้า</Typography>
                  <Chip label="บังคับ" size="small" color="error" sx={{ height: 18, fontSize: 10 }} />
                </Stack>
                <Button
                  size="small" variant="outlined"
                  startIcon={uploadingPO ? <CircularProgress size={13} /> : null}
                  onClick={() => poInputRef.current?.click()}
                  disabled={uploadingPO}
                  sx={{ borderRadius: 2, fontSize: 12 }}
                >
                  {uploadingPO ? 'กำลังอัพโหลด...' : 'เลือกไฟล์'}
                </Button>
                <input
                  ref={poInputRef}
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  style={{ display: 'none' }}
                  onChange={(e) => handleUploadPO(e.target.files?.[0])}
                />
              </Stack>
              {form.po_document_url ? (
                <Stack direction="row" alignItems="center" gap={1}>
                  <Chip
                    label="ดูใบ PO" size="small"
                    onClick={() => window.open(form.po_document_url, '_blank')}
                    sx={{ bgcolor: '#dcfce7', color: '#15803d', fontWeight: 700, cursor: 'pointer', border: '1px solid #bbf7d0' }}
                  />
                  <Button size="small" color="error" sx={{ fontSize: 11, minWidth: 0, p: 0.5 }} onClick={() => setField('po_document_url', '')}>
                    ลบ
                  </Button>
                </Stack>
              ) : (
                <Typography variant="caption" color={errors.po_document_url ? 'error' : 'text.disabled'}>
                  {errors.po_document_url || 'ยังไม่ได้แนบไฟล์ — รองรับ PDF, JPG, PNG (สูงสุด 1MB)'}
                </Typography>
              )}
            </Paper>
          )}

          <Paper variant="outlined" sx={{ borderRadius: 2, p: 2 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>รายการสินค้า / บริการ</Typography>
            <LineItemsTable
              items={form.items}
              onChange={handleItemsChange}
              errors={errors}
            />
          </Paper>

          <Grid container spacing={2.5}>
            <Grid item xs={12} md={7}>
              <Paper variant="outlined" sx={{ borderRadius: 2, p: 2, height: '100%' }}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>หมายเหตุ</Typography>
                <Stack spacing={1.5}>
                  <TextField
                    fullWidth label="หมายเหตุ (แสดงในเอกสาร)" size="small" multiline rows={2}
                    value={form.notes}
                    onChange={(e) => setField('notes', e.target.value)}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                  <TextField
                    fullWidth label="เงื่อนไขการจ่ายเงิน (แสดงในเอกสาร)" size="small"
                    value={form.payment_terms}
                    onChange={(e) => setField('payment_terms', e.target.value)}
                    placeholder="เช่น โอนเงินภายใน 30 วัน / เงินสด"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                  {docType === 'quotation' && (
                    <TextField
                      fullWidth label="เงื่อนไขการเสนอราคา (แต่ละบรรทัด = 1 ข้อ)" size="small" multiline rows={3}
                      value={form.terms_conditions}
                      onChange={(e) => setField('terms_conditions', e.target.value)}
                      placeholder={'Work Period : 30 Days\nWarranty : 6 Month'}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                  )}
                </Stack>
              </Paper>
            </Grid>
            <Grid item xs={12} md={5}>
              <Paper variant="outlined" sx={{ borderRadius: 2, p: 2 }}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>สรุปยอดเงิน</Typography>
                <SummaryBox form={form} onChange={setField} />
              </Paper>
            </Grid>
          </Grid>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, flexShrink: 0 }}>
        <Button onClick={onClose} variant="outlined" sx={{ borderRadius: 2 }}>ยกเลิก</Button>
        <Button
          onClick={submit}
          variant="contained"
          color="secondary"
          disabled={saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
          sx={{ borderRadius: 2 }}
        >
          {saving ? 'กำลังบันทึก...' : 'สร้างเอกสาร'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default CreateSaleDocumentModal;
